import NioMap from '@/components/NioMap'
import { PropType, defineComponent, onUnmounted } from 'vue'
import { ChargeMapContext } from './index.model'
import { MarkerType } from '../iconRender/icon.model'
import { IProps } from '@/components/NioMap/index.model'
import { getPowerResourceTypeVgl } from '@/pages/chargermap/utils/resource'
import { ResourceIconMapVgl } from './mapConfig'
import * as mapvgl from 'mapvgl'
import debounce from 'lodash.debounce'
import { Line, Marker, Point, Size } from '../../contract/map'
import { getIconCanvas, minMax } from './mapUtils'
import { OS } from '@/utils/device'
import { bd09toGcj02 } from '@/utils/coordinateTransform'
import { isInMiniProgram } from '../../utils/acrossAgent'
import { nioJsBridge } from '@nio-wad/universal-bridge-h5'
import { fxResource } from '@/utils/env'
import { isInApp } from '@nio-wad/hooks'

// 业务地图，用于扩展地图方法
export default defineComponent({
  name: 'charge-map',
  props: {
    settings: {
      // 地图设置
      type: Object as PropType<IProps>,
      default: {} as IProps,
    },
    active: {
      type: Boolean,
    },
  },
  emits: [
    'ready',
    'touchend',
    'dragend',
    'dragstart',
    'zoomend',
    'zoomstart',
    'markerClick',
    'vglMarkerClick',
    'polylineClick',
  ],
  setup: (props, { emit }) => {
    // 动画背景的尺寸设定
    const ANIMATION_BG = {
      default: {
        offsetX: -29.5,
        offsetY: -51.5,
        sizeXY: '57px',
      },
      clicked: {
        offsetX: -44,
        offsetY: -77.5,
        sizeXY: '86px',
      },
    }
    const { psAnimation, brand } = process.config as any
    const ANIMATION_BG_URL = psAnimation.replace('${fxResource}', fxResource)
    // console.log('=============ANIMATION_BG_URL=======================')
    // console.log(ANIMATION_BG_URL)
    // console.log('====================================')
    const BLING_BG_SIZE = 40
    const BLING_BG_OVERLAY_OFFSET = -25 // mapv 的 custom overlay 似乎有默认的 25 偏移量，所以需要补上
    const SCALE_THIRD_POINT = 1 / 3

    const win: any = window
    let BMapGL = null as any
    let mapInstance = null as any
    let mapvglViewHeatMap = null as any
    let mapvglView = null as any
    let mapvglViewThird = null as any
    let mapvglViewStationMarker = null as any

    const markerMap = {} as { [key: string]: any }
    const circleBgIdMap = {} as { [key: string]: any } // 圆形背景记录ID
    const polylineMap = {} as { [key: string]: any }
    const polygonMap = {} as { [key: string]: any }
    const pointlayers = {} as { [key: string]: any } // mapvgl点图层
    const wishPowerLayers = {} as { [key: string]: any } // 心愿站点图层
    const vglPolylineLayers = {} as { [key: string]: any } // mapvgl画线图层
    const adjustMarkerWithZoomMap = {} as { [key: string]: any }

    let stationIcon: any = null // 心愿站icon图层
    let originData = '' // 心愿站icon图层 用于切换点还原数据
    let countryBounds = null as any
    let dragStartCenter: any

    const strictBounds = {
      leftUp: { lng: 75.112686, lat: 52.588729 },
      leftDown: { lng: 72.831421, lat: 3.150305 },
      rightUp: { lng: 135.897355, lat: 53.86923 },
      rightDown: { lng: 135.970944, lat: 13.719525 },
    } // 中国区域略小，防止拖到边缘后缩小无法拖动

    /**
     * 调整地图视角
     * @param list
     * @param marginTop
     * @param marginRight
     * @param marginBottom
     * @param marginLeft
     */
    const adjustCenterAndZoom = (
      list: Array<Point>,
      marginTop?: number,
      marginRight?: number,
      marginBottom?: number,
      marginLeft?: number
    ) => {
      // 根据提供的地理区域或坐标获得最佳的地图视野
      if (list.length) {
        const view = list.map((item) => {
          return new BMapGL.Point(item.lng, item.lat)
        })
        mapInstance.setViewport(view, {
          margins: [marginTop || 10, marginRight || 30, marginBottom || 10, marginLeft || 350],
          delay: 100, // 启动视野转移延迟时间
        })
      }
    }

    const adjustCenterAndZoomWithFlyTo = (
      list: Array<Point>,
      marginTop?: number,
      marginRight?: number,
      marginBottom?: number,
      marginLeft?: number
    ) => {
      // 根据提供的地理区域或坐标获得最佳的地图视野 然后flyTo过去
      if (list.length) {
        const view = list.map((item) => {
          return new BMapGL.Point(item.lng, item.lat)
        })
        const viewport = mapInstance.getViewport(view, {
          margins: [marginTop || 10, marginRight || 30, marginBottom || 10, marginLeft || 350],
        })
        mapInstance.flyTo(viewport.center, viewport.zoom)
      }
    }

    /**
     * ChargeMap的个性化默认配置
     */
    const setChargeMapDefaultConfig = () => {
      if (mapInstance) {
        // mapInstance.centerAndZoom(new BMapGL.Point(116.28019, 40.049191), 19) // 必需要设置，否则地图不显示
        setMapBound()
        mapInstance.disableDoubleClickZoom() // 禁止双击zoom
      }
    }

    /**
     * 计算两点距离 返回米
     * @param from
     * @param to
     * @returns
     */
    const calculateDistance = (from: Point, to: Point) => {
      const distance = mapInstance.getDistance(from, to)
      return distance
    }

    const addMarkers = (markers: Marker[], setTop = false) => {
      // 添加marker
      try {
        if (markers && markers.length) {
          markers.forEach((item: Marker, index) => {
            // 20240523 删除，会影响地图上的marker点，影响直接使用clearOverlays清除所有覆盖物
            // 影响点 路径规划切换tab，路径规划回退
            // if (markerMap[item.id]) {
            //   // 地图上已经存在的marker overlay不再创建添加
            //   return
            // }
            const svgUrl = item.icon
            const w = item.size?.width || 30
            const h = item.size?.height || 30
            const myIcon = new BMapGL.Icon(svgUrl, new BMapGL.Size(w, h))
            const point = new BMapGL.Point(item.loc.lng, item.loc.lat)
            const markerPoint = new BMapGL.Marker(point, {
              icon: myIcon,
              enableDragging: false,
              title: item.title || '',
              offset: new BMapGL.Size(0, item.type == MarkerType.Text ? 0 : -h / 2),
            })
            markerPoint.setZIndex(item.zIndex || 10)
            markerPoint.customData = {
              marker: item,
              id: `${index}_${item.id}`,
              instance: markerPoint,
            }
            if (item.id) {
              markerMap[item.id] = markerPoint // 记录marker点，以后可以通过id修改marker
            }
            if (item.label) {
              if (item.type) {
                let label
                const clicked = w >= 50 // 点击状态
                switch (item.type) {
                  case MarkerType.Merged: {
                    // 聚合点里面信息
                    const bw = w / 0.55
                    const bh = h / 0.55
                    label = new BMapGL.Label(item.label, {
                      offset: new BMapGL.Size(-bw / 2, -bh + (13 * w) / 32),
                      position: point,
                    })
                    //设置label样式,使label居中
                    label.setStyle({
                      width: bw + 'px',
                      textAlign: 'center',
                      height: bh + 'px',
                      lineHeight: bh + 'px',
                      color: '#fff',
                      fontSize: '0.12rem',
                      fontWeight: 200,
                      border: '0px',
                      backgroundImage: `url(${svgUrl})`, // 通过给label添加背景，达到label相互覆盖的效果
                      backgroundPosition: '0, 0',
                      backgroundSize: '100% 100%',
                      backgroundColor: 'transparent',
                      transform: 'scale(0.55)',
                      borderRadius: '50%',
                      padding: 0,
                    })
                    break
                  }
                  case MarkerType.Icon:
                    // marker右上角数字
                    label = new BMapGL.Label(item.label, {
                      offset: new BMapGL.Size(0, -h - 4),
                      position: point,
                    })
                    //设置label样式,使label居中
                    label.setStyle({
                      textAlign: 'center',
                      height: '14px',
                      lineHeight: '14px',
                      color: '#000',
                      fontSize: '12px',
                      border: '0px',
                      borderRadius: '14px',
                      padding: '1px 4px',
                      backgroundColor: '#fff',
                      transform: 'scale(0.8333)',
                      boxShadow: '5px 5px 10px rgba(0, 0, 0, 0.05)',
                    })
                    break
                  case MarkerType.Animation:
                    // marker右上角数字
                    label = new BMapGL.Label(item.label, {
                      offset: new BMapGL.Size(
                        clicked ? ANIMATION_BG.clicked.offsetX : ANIMATION_BG.default.offsetX,
                        clicked ? ANIMATION_BG.clicked.offsetY : ANIMATION_BG.default.offsetY
                      ),
                      position: point,
                    })
                    //设置label样式,使label居中
                    label.setStyle({
                      textAlign: 'center',
                      height: clicked ? ANIMATION_BG.clicked.sizeXY : ANIMATION_BG.default.sizeXY,
                      width: clicked ? ANIMATION_BG.clicked.sizeXY : ANIMATION_BG.default.sizeXY,
                      color: '#ffffff00',
                      border: '0px',
                      opacity: '1',
                      borderRadius: '50%',
                      backgroundColor: 'radial-gradient(#ffffff, #48d8fe)', // 不能删掉，删掉渲染透明背景有问题
                      backgroundImage: item.swapStatus == 1 ? `url('${ANIMATION_BG_URL}')` : '',
                      backgroundSize: clicked
                        ? `${ANIMATION_BG.clicked.sizeXY} ${ANIMATION_BG.clicked.sizeXY}`
                        : `${ANIMATION_BG.default.sizeXY} ${ANIMATION_BG.default.sizeXY}`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                    })
                    break
                  case MarkerType.Title:
                    // marker正下方
                    label = new BMapGL.Label(item.label, {
                      offset: new BMapGL.Size(-16, 3),
                      position: point,
                    })
                    //设置label样式,使label居中
                    label.setStyle({
                      textAlign: 'center',
                      height: '14px',
                      lineHeight: '14px',
                      color: '#000',
                      fontSize: '12px',
                      border: '0px',
                      borderRadius: '14px',
                      padding: '1px 4px',
                      backgroundColor: '#fff',
                      transform: 'scale(0.8333)',
                      boxShadow: '5px 5px 10px rgba(0, 0, 0, 0.05)',
                    })
                    break
                  case MarkerType.Text:
                    // marker点下面文案
                    label = new BMapGL.Label(item.label, {
                      offset: new BMapGL.Size(-10, 3),
                      position: point,
                    })
                    //设置label样式,使label居中
                    label.setStyle({
                      textAlign: 'center',
                      height: '14px',
                      lineHeight: '14px',
                      color: '#000',
                      fontSize: '12px',
                      border: '0px',
                      backgroundColor: 'transparent',
                      transform: 'scale(0.55)',
                      fontWeight: '300',
                    })
                    break

                  default:
                    return
                }
                label.setZIndex(item.zIndex || 1)
                markerPoint.setLabel(label)
                markerPoint.setTop(setTop)
              }
            }
            if (item.clickable) {
              markerPoint.addEventListener(
                'click',
                (e: any) => {
                  // 业务地图的marker点击事件
                  emit('markerClick', e?.currentTarget?.customData?.marker)
                },
                false
              )
            }
            mapInstance.addOverlay(markerPoint)
          })
        } else {
          console.log('================marker点数据不能为空====================')
        }
      } catch (error) {
        console.log(`================出错了: ${error}====================`)
      }
    }

    /**
     * 移除marker
     * @param ids
     * @param type
     * @returns
     */
    const removeMarkers = (ids?: string[], type?: string) => {
      if (ids && ids.length) {
        // 指定移除的marker
        if (type === 'exclude') {
          // 移除除了ids中的marker
          Object.keys(markerMap).forEach((id) => {
            if (ids?.indexOf(id) === -1) {
              mapInstance.removeOverlay(markerMap[id])
              delete markerMap[id]
            }
          })
        } else {
          // 移除ids中的marker
          ids.forEach((id) => {
            mapInstance.removeOverlay(markerMap[id])
            delete markerMap[id]
          })
        }
      } else {
        // 移除所有marker
        Object.keys(markerMap).forEach((id) => {
          mapInstance.removeOverlay(markerMap[id])
          delete markerMap[id]
        })
      }
      return markerMap
    }

    const flyToCenterAndZoom = (center: Point, zoom?: number) => {
      // 动画效果飞到指定地点和zoom
      if (zoom) {
        // 注意：flyTo会触发zoomend事件
        mapInstance.flyTo(center, zoom)
      } else {
        mapInstance.panTo(center)
      }
    }

    const getMarkerById = (id: string) => {
      // 根据ID获取marker
      return markerMap[id]
    }

    const addSurroundCircle = (center: Point, radius: number, id: string) => {
      if (circleBgIdMap[id]) {
        return
      }
      const options =
        brand === 'fy'
          ? {
              strokeWeight: 0,
              strokeColor: '#CFC7FF',
              strokeOpacity: 0,
              fillOpacity: 0.36,
              fillColor: '#CFC7FF',
            }
          : {
              strokeWeight: 0,
              strokeColor: '#00659B',
              strokeOpacity: 0,
              fillOpacity: 0.08,
              fillColor: '#00659B',
            }
      // 指定点添加环绕效果，可添加多个
      const circleBg = new BMapGL.Circle(new BMapGL.Point(center.lng, center.lat), radius, options)
      circleBg.disableMassClear()
      mapInstance.addOverlay(circleBg)
      circleBgIdMap[id] = circleBg
    }

    const removeAllSurroundCircle = (ids?: string[], exclude?: boolean) => {
      if (exclude && ids && ids.length) {
        Object.keys(circleBgIdMap).forEach((id: string) => {
          if (ids.indexOf(id) === -1) {
            const circleBg = circleBgIdMap[id]
            mapInstance.removeOverlay(circleBg)
            delete circleBgIdMap[id]
            const marker = markerMap[id]
            marker?.enableMassClear()
          }
        })
      } else {
        const tempIds = ids || Object.keys(circleBgIdMap)
        tempIds.forEach((id: string) => {
          const circleBg = circleBgIdMap[id]
          mapInstance.removeOverlay(circleBg)
          delete circleBgIdMap[id]
          const marker = markerMap[id]
          marker?.enableMassClear()
        })
      }
    }

    const changeMarker = (id: string, size: Size, zIndex?: number, icon?: string) => {
      if (!markerMap[id]) {
        return
      }
      const marker = markerMap[id]
      const markerIcon = icon ? icon : marker.customData.marker.icon
      if (markerIcon) {
        marker.customData.marker.size = size
        const svgUrl = markerIcon
        marker.setIcon(new BMapGL.Icon(svgUrl, new BMapGL.Size(size.width, size.height)))
        marker.setOffset(new BMapGL.Size(0, -size.height / 2))
        if (marker.customData.marker.label) {
          let label = marker.getLabel()
          if (!marker.getLabel()) {
            label = new BMapGL.Label(marker.customData.marker.label, {
              position: new BMapGL.Point(
                marker.customData.marker.loc.lng,
                marker.customData.marker.loc.lat
              ),
            })
          }
          if (marker.customData.marker.type === MarkerType.Animation) {
            const clicked = size.width >= 50
            // 带动画，改变icon大小
            label.setOffset(
              new BMapGL.Size(
                clicked ? ANIMATION_BG.clicked.offsetX : ANIMATION_BG.default.offsetX,
                clicked ? ANIMATION_BG.clicked.offsetY : ANIMATION_BG.default.offsetY
              )
            )
            label.setStyle({
              textAlign: 'center',
              height: clicked ? ANIMATION_BG.clicked.sizeXY : ANIMATION_BG.default.sizeXY,
              width: clicked ? ANIMATION_BG.clicked.sizeXY : ANIMATION_BG.default.sizeXY,
              color: '#ffffff00',
              border: '0px',
              opacity: '1',
              borderRadius: '50%',
              backgroundColor: 'radial-gradient(#ffffff, #48d8fe)', // 不能删掉，删掉渲染透明背景有问题
              backgroundImage:
                marker.customData.marker.swapStatus == 1 ? `url('${ANIMATION_BG_URL}')` : '',
              backgroundSize: clicked
                ? `${ANIMATION_BG.clicked.sizeXY} ${ANIMATION_BG.clicked.sizeXY}`
                : `${ANIMATION_BG.default.sizeXY} ${ANIMATION_BG.default.sizeXY}`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
            })
          } else {
            // 不带动画，改变icon大小
            label.setOffset(new BMapGL.Size(0, -size.height - 4))
          }
          marker.setLabel(label)
        }
        if (zIndex) {
          marker.setZIndex(zIndex)
        }
      }
    }

    /**
     * 改变换电站正在换电的UI显示状态
     */
    const changeChargingStatus = (chargingStatus: any[]) => {
      chargingStatus.forEach((state) => {
        if (!markerMap[state.swapId]) {
          return
        }
        const marker = markerMap[state.swapId]
        marker.customData.marker.swapStatus = state.swapStatus
        const label = marker.getLabel()
        const clicked = marker.customData.marker.size.width >= 50
        label.setOffset(
          new BMapGL.Size(
            clicked ? ANIMATION_BG.clicked.offsetX : ANIMATION_BG.default.offsetX,
            clicked ? ANIMATION_BG.clicked.offsetY : ANIMATION_BG.default.offsetY
          )
        )
        label.setStyle({
          textAlign: 'center',
          height: clicked ? ANIMATION_BG.clicked.sizeXY : ANIMATION_BG.default.sizeXY,
          width: clicked ? ANIMATION_BG.clicked.sizeXY : ANIMATION_BG.default.sizeXY,
          color: '#ffffff00',
          border: '0px',
          opacity: '1',
          borderRadius: '50%',
          backgroundColor: 'radial-gradient(#ffffff, #48d8fe)', // 不能删掉，删掉渲染透明背景有问题
          backgroundImage:
            marker.customData.marker.swapStatus == 1 ? `url('${ANIMATION_BG_URL}')` : '',
          backgroundSize: clicked
            ? `${ANIMATION_BG.clicked.sizeXY} ${ANIMATION_BG.clicked.sizeXY}`
            : `${ANIMATION_BG.default.sizeXY} ${ANIMATION_BG.default.sizeXY}`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        })
        marker.setLabel(label)
      })
    }

    /**
     *
     * @param e 点击vgl icon的某个点
     */
    const clickMapVglMarker = (e: any, runEmit = false) => {
      const dataTemp = JSON.parse(originData)
      stationIcon?.setData(dataTemp)
      if (e) {
        dataTemp[e.dataIndex].properties.width = 60
        dataTemp[e.dataIndex].properties.height = 64
        dataTemp[e.dataIndex].properties.offset = [0, -32]
        stationIcon?.setData(dataTemp)
        runEmit && emit('vglMarkerClick', { marker: e?.dataItem?.properties?.marker, e })
      }
    }

    // 还原数据
    const resetMapVglMarkers = () => {
      stationIcon?.setData(JSON.parse(originData))
    }

    const hideMapVglMarkers = () => {
      if (stationIcon) {
        mapvglViewStationMarker.hideLayer(stationIcon)
      }
    }

    const showMapVglMarkers = () => {
      if (stationIcon) {
        mapvglViewStationMarker.showLayer(stationIcon)
      }
    }

    const addMapVglMarkers = (markers: Point[], options?: any) => {
      if (markers && markers.length) {
        if (!stationIcon) {
          initMapVglMarkerLayer()
        }
        const data: any[] = []
        markers.forEach((item: Point) => {
          const dataItem = {
            geometry: {
              type: 'Point',
              coordinates: [item.lng, item.lat, 10],
            },
            properties: {
              time: Math.random() * 100,
              marker: item,
              icon: ResourceIconMapVgl[getPowerResourceTypeVgl(item.type)],
            } as any,
          }
          dataItem.properties = Object.assign(dataItem.properties, options)
          data.push(dataItem)
        })
        originData = JSON.stringify(data)
        stationIcon.setData(data)
        mapvglViewStationMarker.showLayer(stationIcon)
      }
    }

    /**
     * 初始化mapvgl的图层
     */
    const initMapVglMarkerLayer = () => {
      // 第三层
      mapvglViewStationMarker = new mapvgl.View({
        map: mapInstance,
      })
      stationIcon = new mapvgl.IconLayer({
        blend: 'lighter',
        renderOrder: 100,
        unit: 'px',
        width: 40,
        height: 43,
        offset: [0, -18.5],
        enablePicked: true, // 是否可以拾取
        autoSelect: false, // 根据鼠标位置来自动设置选中项
        onClick: (e: any) => {
          if (!e?.dataItem?.properties?.marker?.id) return
          clickMapVglMarker(e, true)
        },
      })
      mapvglViewStationMarker.addLayer(stationIcon)
    }

    /**
     * 通过百度地图获取指定区域经纬度，中心点和zoom
     * @param area 地名
     * @returns
     */
    const getAreaBoundary = (area: string, backupName?: string) => {
      return new Promise<{ [key: string]: any }>((resolve, reject) => {
        const mapBoundary = new BMapGL.Boundary()
        mapBoundary.get(area, (res: any) => {
          let boundaryArr: any[] = []
          const boundaryList: any[] = []
          if (res?.boundaries?.length) {
            res.boundaries.forEach((boundary: string) => {
              const temp = boundary.split(';').map((lnglatStr: string) => {
                const point = lnglatStr.split(', ')
                return new BMapGL.Point(point[0], point[1])
              })
              boundaryList.push(temp)
              boundaryArr = boundaryArr.concat(temp)
            })
            const viewport = mapInstance.getViewport(boundaryArr)
            resolve({
              boundaries: boundaryList, // 原始数据，多条
              boundary: boundaryArr, // 多条合并为一条
              center: viewport.center,
              zoom: viewport.zoom,
            })
          } else {
            if (backupName) {
              // 搜不到使用备用名称搜索
              mapBoundary.get(backupName, (res: any) => {
                if (res?.boundaries?.length) {
                  res.boundaries.forEach((boundary: string) => {
                    const temp = boundary.split(';').map((lnglatStr: string) => {
                      const point = lnglatStr.split(', ')
                      return new BMapGL.Point(point[0], point[1])
                    })
                    boundaryList.push(temp)
                    boundaryArr = boundaryArr.concat(temp)
                  })
                  const viewport = mapInstance.getViewport(boundaryArr)
                  resolve({
                    boundaries: boundaryList, // 原始数据，多条
                    boundary: boundaryArr, // 多条合并为一条
                    center: viewport.center,
                    zoom: viewport.zoom,
                  })
                } else {
                  reject()
                }
              })
            } else {
              reject()
            }
          }
        })
      })
    }

    const getCurrentPosition = (configOptions?: { [key: string]: any }) => {
      // 获取定位
      return new Promise<Point>((resolve, reject) => {
        try {
          if (win.geoLoc) {
            resolve(win.geoLoc)
          } else {
            // 当多个地图同时加载多并发及多次点击定位请求定位
            if (win.debounceFn) {
              win.debounceFn.cancel()
              delete win.debounceFn
            }
            win.debounceFn = debounce(
              () => {
                if (isInMiniProgram() || isInApp) {
                  // 微信小程序中使用Universal Bridge获取定位
                  nioJsBridge
                    .getLocation({
                      data: {
                        type: 'gcj-02',
                      },
                    })
                    .then((res: any) => {
                      if (res.result_code === 'success' && res.data) {
                        // 获取定位成功
                        console.log('====================================')
                        console.log('小程序|app获取定位成功', res)
                        console.log('====================================')
                        win.geoLoc = { lng: res.data.longitude, lat: res.data.latitude }
                        resolve(win.geoLoc)
                      } else {
                        console.log('====================================')
                        console.log('小程序|app获取定位失败', res)
                        console.log('====================================')
                        reject()
                      }
                    })
                    .catch(() => {
                      console.log('====================================')
                      console.log('小程序|app获取定位失败')
                      console.log('====================================')
                      reject()
                    })
                } else {
                  // 配置参考 https://mapopen-pub-jsapi.bj.bcebos.com/jsapi/reference/jsapi_webgl_1_0.html#a8b42
                  const positionOptions = Object.assign({}, configOptions || {})
                  const geolocation = new BMapGL.Geolocation()
                  geolocation.getCurrentPosition((r: any) => {
                    const statusCode = geolocation.getStatus()
                    console.log('==========getCurrentPosition====定位状态======================')
                    console.log(statusCode)
                    console.log(r)
                    console.log('====================================')
                    if (r && r.latitude && r.longitude) {
                      const bd09Location = { lng: r.longitude, lat: r.latitude }
                      win.geoLoc = bd09toGcj02(bd09Location)
                      console.log('Geolocation定位成功', win.geoLoc)
                      resolve(win.geoLoc)
                    } else {
                      // 使用ip定位兜底
                      const myCity = new BMapGL.LocalCity()
                      myCity.get((r: any) => {
                        console.log('=========LocalCity===========================')
                        console.log(r)
                        console.log('====================================')
                        if (r && r.center && r.center.lat && r.center.lng) {
                          const bd09Location = { lng: r.center.lng, lat: r.center.lat }
                          win.geoLoc = bd09toGcj02(bd09Location)
                          console.log('LocalCity定位成功', win.geoLoc)
                          resolve(win.geoLoc)
                        } else {
                          reject()
                          console.log('================定位失败====================')
                        }
                      })
                    }
                  }, positionOptions)
                }
              },
              0,
              { leading: false }
            )
            win.debounceFn()
          }
        } catch (error) {
          console.log(`================出错了: ${error}====================`)
        }
      })
    }

    /**
     * 打断已经开始的bling效果
     */
    const interruptBling = () => {
      Object.keys(markerMap).forEach((id: string) => {
        if (Object.prototype.hasOwnProperty.call(markerMap, id) && id.indexOf('_bling') > -1) {
          mapInstance.removeOverlay(markerMap[id])
          delete markerMap[id]
        }
      })
    }

    /**
     * 画换电完成的动画
     * @param markers 点位
     * @param ratio 放大比例
     */
    const addBlingAnimation = (markers: Point[], ratio = 1) => {
      if (markers?.length > 0) {
        const uuids: string[] = []
        markers.forEach((item: Point, index) => {
          const uuid = `${item.id || new Date().getTime()}_${index}_bling`
          uuids.push(uuid)
          const point = new BMapGL.Point(item.lng, item.lat)
          const layer = new BMapGL.CustomOverlay(createBlinkSpot, {
            enableMassClear: true,
            offsetY: BLING_BG_OVERLAY_OFFSET + (BLING_BG_SIZE * ratio) / 2,
            point,
            properties: {
              height: BLING_BG_SIZE * ratio + 'px',
              width: BLING_BG_SIZE * ratio + 'px',
            },
          })
          markerMap[uuid] = layer
          mapInstance.addOverlay(layer)
        })

        // 一起删除，减少定时器开销
        setTimeout(() => {
          uuids.forEach((uuid: string) => {
            if (Object.prototype.hasOwnProperty.call(markerMap, uuid)) {
              mapInstance.removeOverlay(markerMap[uuid])
              delete markerMap[uuid]
            }
          })
        }, 4300)
      }
      function createBlinkSpot(features: any) {
        const div = document.createElement('div')
        div.classList.add('swap-blink-spot')
        div.style.height = features.height || BLING_BG_SIZE + 'px'
        div.style.width = features.width || BLING_BG_SIZE + 'px'
        div.style.borderRadius = features.height || BLING_BG_SIZE + 'px'
        return div
      }
    }

    // 画出多边形覆盖物
    // https://mapopen-pub-jsapi.bj.bcebos.com/jsapi/reference/jsapi_webgl_1_0.html#a3b12
    const addPolygon = (polygon: Line[]) => {
      try {
        if (polygon && polygon.length) {
          const returnPolygonMap: any = {} // 新增部分
          polygon.forEach((item: Line, index: number) => {
            const id = `${item.id && item.id + '_'}${index}`
            const points = item.points.map((pi: Point) => {
              return new BMapGL.Point(pi.lng, pi.lat)
            })
            const customStyle = Object.assign(
              {
                strokeColor: '#00BEBE',
                strokeWeight: 0,
                strokeOpacity: 0.3,
                // strokeStyle: 'dashed',
                fillColor: '#00BEBE',
                fillOpacity: 0.15,
              },
              item.options
            )
            const polygon = new BMapGL.Polygon(points, customStyle)
            polygon.customData = {
              polygon: item,
              id,
              instance: polygon,
            }
            item.disableMassClear && polygon.disableMassClear()
            polygonMap[id] = polygon
            returnPolygonMap[id] = polygon
            mapInstance.addOverlay(polygon)
          })
          return returnPolygonMap
        } else {
          console.log('================路线数据不能为空====================')
        }
      } catch (error) {
        console.log(`================出错了: ${error}====================`)
      }
    }

    /**
     * 批量操作点图层的显示或隐藏
     * @param id
     * @param show
     * @returns
     */
    const operatePointLayer = (id: string, show: boolean) => {
      let exist = false
      Object.keys(pointlayers).forEach((key: string) => {
        // 模糊匹配
        if (key.indexOf(id) > -1) {
          if (show) {
            if (pointlayers[key]?.controller === 'mapvglView') {
              mapvglView.showLayer(pointlayers[key].layer)
            } else if (pointlayers[key]?.controller === 'mapvglViewThird') {
              mapvglViewThird.showLayer(pointlayers[key].layer)
            }
            // mapvglView?.showLayer(pointlayers[key])
            // mapvglViewThird?.showLayer(pointlayers[key])
          } else {
            if (pointlayers[key]?.controller === 'mapvglView') {
              mapvglView.hideLayer(pointlayers[key].layer)
            } else if (pointlayers[key]?.controller === 'mapvglViewThird') {
              mapvglViewThird.hideLayer(pointlayers[key].layer)
            }
            // mapvglView?.hideLayer(pointlayers[key])
            // mapvglViewThird?.hideLayer(pointlayers[key])
          }
          // show ? mapvglView?.showLayer(pointlayers[key]) : mapvglView?.hideLayer(pointlayers[key])
          exist = true
        }
      })
      return exist
    }
    // 画点图层
    const setPointLayer = (
      points: Point[],
      color: string,
      size: number,
      id: string,
      zIndex = 10,
      display = true
    ) => {
      if (!operatePointLayer(id, display)) {
        const layer = new mapvgl.IconLayer({
          width: size,
          height: size,
          offset: [0, -size / 2],
          icon: getIconCanvas(color),
          scale: 1,
          renderOrder: zIndex,
        })
        // const layer = new mapvgl.PointLayer({
        //   color: color,
        //   borderWidth: size * 0.1,
        //   borderColor: '#fff',
        //   size: size,
        //   renderOrder: zIndex,
        //   offset: [0, -size / 2],
        // })
        mapvglView?.addLayer(layer)
        const data = points.map((point: Point) => {
          return {
            geometry: {
              type: 'Point',
              coordinates: [point.lng, point.lat],
            },
          }
        })
        // 关联图层与数据，享受震撼的可视化效果
        layer.setData(data)
        // cache
        // pointlayers[id] = layer
        pointlayers[id] = {
          layer,
          controller: 'mapvglView',
        }
        !display && mapvglView?.hideLayer(layer)
      }
    }

    // 画点三方图层
    const setThirdPointLayer = (
      points: Point[],
      color: string,
      size: number,
      id: string,
      zIndex = 5,
      display = true
    ) => {
      if (!operatePointLayer(id, display)) {
        const layer = new mapvgl.PointLayer({
          color: color || '#93E1DD',
          size: size * SCALE_THIRD_POINT,
          renderOrder: zIndex,
          offset: [0, (-size * SCALE_THIRD_POINT) / 2],
        })
        mapvglViewThird?.addLayer(layer)
        const data = points.map((point: Point) => {
          return {
            geometry: {
              type: 'Point',
              coordinates: [point.lng, point.lat],
            },
          }
        })
        // 关联图层与数据，享受震撼的可视化效果
        layer.setData(data)
        // cache
        // pointlayers[id] = layer
        pointlayers[id] = {
          layer,
          controller: 'mapvglViewThird',
        }
        !display && mapvglViewThird?.hideLayer(layer)
      }
    }

    // // 清空所有点图层
    // const resizeAllPointLayer = (size: number) => {
    //   for (const key of Object.keys(pointlayers)) {
    //     const layer = pointlayers[key]
    //     const layerStyle = layer.getOptions()
    //     if (key.indexOf('third') > -1) {
    //       layerStyle.width = size * SCALE_THIRD_POINT
    //       layerStyle.height = size * SCALE_THIRD_POINT
    //       layerStyle.offset = [0, (-size * SCALE_THIRD_POINT) / 2]
    //     } else {
    //       layerStyle.width = size
    //       layerStyle.height = size
    //       layerStyle.offset = [0, -size / 2]
    //     }
    //     layer.setOptions(layerStyle)
    //   }
    // }

    const resizeAllPointLayer = (size: number) => {
      for (const key of Object.keys(pointlayers)) {
        // const layer = pointlayers[key]
        const layer = pointlayers[key].layer
        const layerStyle = layer.getOptions()
        if (key.indexOf('third') > -1) {
          layerStyle.size = size * SCALE_THIRD_POINT // 设定三方桩点现实大小只有正常的一半
        } else {
          // layerStyle.size = size
          layerStyle.width = size
          layerStyle.height = size
          layerStyle.offset = [0, -size / 2]
        }
        layer.setOptions(layerStyle)
      }
    }

    // 画出多条路线图
    // https://lbsyun.baidu.com/cms/jsapi/reference/jsapi_reference.html#a3b11
    const addPolyline = (polyline: Line[]) => {
      try {
        if (polyline && polyline.length) {
          polyline.forEach((item: Line, index: number) => {
            const id = `${item.id && item.id + '_'}${index}`
            const points = item.points.map((pi: Point) => {
              return new BMapGL.Point(pi.lng, pi.lat)
            })
            const customStyle = Object.assign(
              {
                strokeColor: '#00BEBE',
                strokeWeight: 1,
                strokeOpacity: 1,
              },
              item.options
            )
            const polyline = new BMapGL.Polyline(points, customStyle)
            polyline.customData = {
              polyline: item,
              id,
              instance: polyline,
            }
            item.disableMassClear && polyline.disableMassClear()
            if (item.clickable) {
              polyline.addEventListener(
                'click',
                (e: any) => {
                  emit('polylineClick', e)
                },
                false
              )
            }
            polylineMap[id] = polyline
            mapInstance.addOverlay(polyline)
          })
          return polylineMap
        } else {
          console.log('================路线数据不能为空====================')
        }
      } catch (error) {
        console.log(`================出错了: ${error}====================`)
      }
    }

    const clearMarkers = (ids?: string[]) => {
      if (ids && ids.length) {
        ids.forEach((id) => {
          const marker = markerMap[id]
          mapInstance.removeOverlay(marker)
          delete markerMap[id]
        })
      } else {
        for (const key of Object.keys(markerMap)) {
          const marker = markerMap[key]
          mapInstance.removeOverlay(marker)
          delete markerMap[key]
        }
      }
    }

    const adjustSizeWithZoom = (zoom: number) => {
      // 根据zoom调整marker的size
      for (const id of Object.keys(adjustMarkerWithZoomMap)) {
        const marker = adjustMarkerWithZoomMap[id]
        const markerW = marker.customData.marker.size.width || 30
        const markerH = marker.customData.marker.size.height || 30
        changeMarker(id, {
          width: minMax((markerW / 11) * zoom, 30, 40),
          height: minMax((markerH / 11) * zoom, 30, 40),
        })
      }
    }

    /**
     * 批量操作线图层的显示或隐藏
     * @param id
     * @param show
     * @returns
     */
    const operatePolylineLayer = (id: string, show: boolean) => {
      let exist = false
      Object.keys(vglPolylineLayers).forEach((key: string) => {
        // 模糊匹配
        if (key.indexOf(id) > -1) {
          if (show) {
            mapvglViewThird.showLayer(vglPolylineLayers[key])
          } else {
            mapvglViewThird.hideLayer(vglPolylineLayers[key])
          }
          // show
          //   ? mapvglView?.showLayer(vglPolylineLayers[key])
          //   : mapvglView?.hideLayer(vglPolylineLayers[key])
          exist = true
        }
      })
      return exist
    }

    /**
     * 使用mapvgl画线
     * @param id
     * @param polyline
     */
    const addPolylineMapvgl = (id: string, polyline?: Line[]) => {
      if (Object.prototype.hasOwnProperty.call(vglPolylineLayers, id)) {
        vglPolylineLayers[id].setOptions({
          width:
            polyline && polyline.length && polyline[0].options
              ? polyline[0].options.strokeWeight
              : 0.6,
        })
        operatePolylineLayer(id, true)
      } else if (polyline && polyline.length) {
        const dataLine: any[] = []
        // 第一次加载
        // 多条线画在一个图层
        polyline.forEach((item: Line) => {
          const points = item.points.map((pi: Point) => {
            return [pi.lng, pi.lat]
          })
          dataLine.push({
            geometry: {
              type: 'LineString',
              coordinates: points,
            },
            properties: {
              time: Math.random() * 100,
              color: item.options?.strokeColor,
            },
          })
        })
        const layer = new mapvgl.LineLayer({
          renderOrder: 8,
          blend: 'lighter',
          width: polyline[0].options?.strokeWeight,
        })
        layer.setData(dataLine)
        mapvglViewThird.addLayer(layer)
        // cache
        vglPolylineLayers[id] = layer
      }
    }

    /**
     * 添加心愿站周边3km范围圈
     * @param {center} center 中心点坐标
     * @param {radius} radius 半径
     * @param {id} id 资源id
     */
    const addSwapCircleAll = (center: Point, radius: number, id: string) => {
      // 指定点添加环绕效果，可添加多个
      const circleBg = new BMapGL.Circle(new BMapGL.Point(center.lng, center.lat), radius, {
        strokeWeight: 0,
        strokeColor: 'rgba(28,119,149,0)',
        strokeOpacity: 0,
        fillColor: 'rgba(28,119,149,0.08)',
        fillOpacity: 0.08,
        enablePicked: false, // 是否可以拾取
      })

      if (circleBgIdMap[id]) {
        mapInstance.removeOverlay(circleBgIdMap[id])
      }
      // circleBg.disableMassClear()
      circleBg.enableMassClear()
      mapInstance.addOverlay(circleBg)
      circleBgIdMap[id] = circleBg
    }

    /**
     * 清空所有心愿站图层
     * clearOverlays方法中不会清除，需要手动清除
     * @param ids
     */
    const clearWishPowerLayer = (ids?: string[]) => {
      if (ids && ids.length) {
        ids.forEach((id) => {
          Object.keys(wishPowerLayers)?.forEach((layerId: string) => {
            if (layerId.indexOf(id) > -1) {
              mapvglViewHeatMap.hideLayer(wishPowerLayers[layerId])
            }
          })
        })
      } else {
        for (const key of Object.keys(wishPowerLayers)) {
          mapvglViewHeatMap.hideLayer(wishPowerLayers[key])
        }
      }
    }

    /**
     * 重新设置heat图层
     * @param key
     * @param size
     * @param clusterRadius
     */
    const resetHeatConfig = (
      key: string,
      size?: number,
      clusterRadius?: number,
      min?: number,
      max?: number
    ) => {
      size && wishPowerLayers[key].setOptions({ size })
      clusterRadius && wishPowerLayers[key].setOptions({ gridSize: clusterRadius })
      min && min >= 0 && wishPowerLayers[key].setOptions({ min })
      max && max >= 0 && wishPowerLayers[key].setOptions({ max })
    }

    /**
     * 画方点心愿地址
     * @param points 点位
     * @param id
     * @param options 配置参数
     */
    const setWishPowerLayer = (points: Point[], id: string, options: any = {}) => {
      if (Object.prototype.hasOwnProperty.call(wishPowerLayers, id)) {
        // 如果已经加载只修改图层参数设置
        resetHeatConfig(id, options.size, options.gridSize, options.min, options.max)
        mapvglViewHeatMap.showLayer(wishPowerLayers[id])
      } else {
        // 第一次加载
        const data = points.map((point: Point) => {
          return {
            geometry: {
              type: 'Point',
              coordinates: [point.lng, point.lat, 1],
            },
            properties: {
              time: Math.random() * 100,
              renderOrder: 1,
            },
          }
        })
        const layer = new mapvgl.HeatPointLayer(
          Object.assign(
            {
              renderOrder: 1,
              gridSize: 500,
              shape: 'square',
              unit: 'px',
              size: 3,
              borderColor: [0, 0, 0, 0],
              borderWidth: 1,
              style: 'grid',
              min: 0,
              max: 4, // 每个聚合点的最大值
              gradient: {},
              data,
            },
            options
          )
        )
        mapvglViewHeatMap?.addLayer(layer)
        // cache
        wishPowerLayers[id] = layer
      }
    }

    // 清空所有点图层
    const clearAllPointLayer = (ids?: string[]) => {
      if (!mapvglView && !mapvglViewThird) {
        return
      }
      if (ids && ids.length) {
        ids.forEach((id) => {
          if (pointlayers[id]?.controller === 'mapvglView') {
            mapvglView.hideLayer(pointlayers[id].layer)
          } else if (pointlayers[id]?.controller === 'mapvglViewThird') {
            mapvglViewThird.hideLayer(pointlayers[id].layer)
          }
          // mapvglView.hideLayer(pointlayers[id])
          // mapvglViewThird.hideLayer(pointlayers[id])
        })
      } else {
        for (const key of Object.keys(pointlayers)) {
          if (pointlayers[key]?.controller === 'mapvglView') {
            mapvglView.hideLayer(pointlayers[key].layer)
          } else if (pointlayers[key]?.controller === 'mapvglViewThird') {
            mapvglViewThird.hideLayer(pointlayers[key].layer)
          }
          // mapvglView.hideLayer(pointlayers[key])
          // mapvglViewThird.hideLayer(pointlayers[key])
        }
      }
    }

    // 清除所有点图层
    const removePointLayer = (ids?: string[]) => {
      if (!mapvglView && !mapvglViewThird) {
        return
      }
      if (ids && ids.length) {
        ids.forEach((id) => {
          if (pointlayers[id]?.controller === 'mapvglView') {
            mapvglView.removeLayer(pointlayers[id].layer)
          } else if (pointlayers[id]?.controller === 'mapvglViewThird') {
            mapvglViewThird.removeLayer(pointlayers[id].layer)
          }
          // mapvglView.removeLayer(pointlayers[id])
          // mapvglViewThird.removeLayer(pointlayers[id])
          delete pointlayers[id]
        })
      } else {
        for (const key of Object.keys(pointlayers)) {
          if (pointlayers[key]?.controller === 'mapvglView') {
            mapvglView.removeLayer(pointlayers[key].layer)
          } else if (pointlayers[key]?.controller === 'mapvglViewThird') {
            mapvglViewThird.removeLayer(pointlayers[key].layer)
          }
          // mapvglView.removeLayer(pointlayers[key])
          // mapvglViewThird.removeLayer(pointlayers[key])
          delete pointlayers[key]
        }
      }
      console.log('====================================')
      console.log('清除所有点图层', pointlayers)
      console.log('====================================')
    }

    /**
     * 向外传递的方法
     */
    const context = {
      adjustCenterAndZoom,
      adjustCenterAndZoomWithFlyTo,
      calculateDistance,
      addMarkers,
      removeMarkers,
      flyToCenterAndZoom,
      getMarkerById,
      addSurroundCircle,
      removeAllSurroundCircle,
      changeMarker,
      changeChargingStatus,
      resetMapVglMarkers,
      hideMapVglMarkers,
      showMapVglMarkers,
      addMapVglMarkers,
      getAreaBoundary,
      getCurrentPosition,
      interruptBling,
      addBlingAnimation,
      addPolygon,
      operatePointLayer,
      setPointLayer,
      setThirdPointLayer,
      resizeAllPointLayer,
      addPolyline,
      clearMarkers,
      adjustSizeWithZoom,
      operatePolylineLayer,
      addPolylineMapvgl,
      addSwapCircleAll,
      clearWishPowerLayer,
      setWishPowerLayer,
      clearAllPointLayer,
      removePointLayer,
    } as ChargeMapContext

    // 配置mapVGL
    const configMapvglView = () => {
      if (OS.isIpad) {
        mapvglViewThird = new mapvgl.View({
          map: mapInstance,
        })
        // 第二层
        mapvglView = new mapvgl.View({
          map: mapInstance,
        })
        // 存在性能问题，不另外添加图层控制器
        mapvglViewHeatMap = mapvglView
      } else {
        // 第一层
        mapvglViewHeatMap = new mapvgl.View({
          map: mapInstance,
        })
        mapvglViewThird = new mapvgl.View({
          map: mapInstance,
        })
        // 第二层
        mapvglView = new mapvgl.View({
          map: mapInstance,
        })
      }
      mapInstance.addEventListener(
        'onglmoduleloaded',
        () => {
          // (window as any).mapInstance = mapInstance
          // remove icon xiezilou
          mapInstance._webglMapScene._painter._iconTextureAtlas.addTexture = () => {
            return { width: 0, height: 0 }
          }
          // remove 内部
          mapInstance._webglMapScene._painter.drawIndoorBase = () => {}
        },
        false
      )
    }

    const setMapBound = () => {
      const southWest = new BMapGL.Point(strictBounds.leftDown.lng, strictBounds.leftDown.lat)
      const northEast = new BMapGL.Point(strictBounds.rightUp.lng, strictBounds.rightUp.lat)
      countryBounds = new BMapGL.Bounds(southWest, northEast)
    }

    // 拖拽地图结束后，返还中心点坐标
    const mapDragEnd = () => {
      const center = mapInstance.getCenter()
      if (countryBounds.containsPoint(center)) {
        const geoc = new BMapGL.Geocoder()
        geoc.getLocation(center, (res: any) => {
          const center = {
            lng: res.point.lng,
            lat: res.point.lat,
          } as Point
          emit('dragend', center)
        })
      } else {
        const level = mapInstance.getZoom()
        if (level === props.settings?.minZoom) {
          emit('zoomend')
        } else {
          mapInstance.panTo(dragStartCenter)
        }
      }
    }

    // 拖拽地图开始
    const mapDragStart = () => {
      dragStartCenter = mapInstance.getCenter()
    }

    const mapZoomEnd = () => {
      // const level = mapInstance.getZoom()
      emit('zoomend')
    }

    const mapZoomStart = () => {
      // const level = mapInstance.getZoom()
      emit('zoomstart')
    }

    // 向上传递地图事件
    const onNioMapReady = (map: any) => {
      BMapGL = window.BMapGL
      // 业务地图加载完成
      mapInstance = map.mapInstance
      configMapvglView()
      setChargeMapDefaultConfig() // TODO: 业务地图的默认配置
      // 配置事件监听
      initEventListener()
      const readyContext: ChargeMapContext = Object.assign({}, map.context, context)
      emit('ready', readyContext) // 必须在setMapDefaultConfig后ready 否则大公鸡不会适配屏幕
    }
    // 启动相关事件监听
    const initEventListener = () => {
      mapInstance?.addEventListener('dragend', mapDragEnd, false)
      mapInstance?.addEventListener('dragstart', mapDragStart, false)
      mapInstance?.addEventListener('zoomend', mapZoomEnd, false)
      mapInstance?.addEventListener('zoomstart', mapZoomStart, false)
    }

    // unmounted时摧毁相关事件监听
    const destroyEventListener = () => {
      mapInstance?.removeEventListener('dragend', mapDragEnd, false)
      mapInstance?.removeEventListener('dragstart', mapDragStart, false)
      mapInstance?.removeEventListener('zoomend', mapZoomEnd, false)
      mapInstance?.removeEventListener('zoomstart', mapZoomStart, false)
    }

    onUnmounted(() => {
      destroyEventListener()
    })
    return () => <NioMap onReady={onNioMapReady}></NioMap>
  },
})
